import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, downloadUrl } from '../api/client'
import { useAuth } from '../context/useAuth'
import { useToast } from '../context/useToast'
import { usePreferences } from '../context/usePreferences'
import ABPlayer from '../components/ABPlayer'
import Waveform from '../components/Waveform'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import Badge, { getGenreBadgeVariant } from '../components/ui/Badge'
import StatCard from '../components/ui/StatCard'
import SectionTitle from '../components/ui/SectionTitle'
import { PrimaryButton, SecondaryButton, DangerButton } from '../components/ui/Button'
import { AccordionSection } from '../components/ui/Accordion'
import { WaveformSkeleton } from '../components/ui/Skeleton'
import ProgressBar from '../components/ProgressBar'

const POLL_INTERVAL_MS = 1500
const STATUS_PROCESSING = ['uploaded', 'analyzing', 'mastering']

// Empfohlene Pipeline: Upload → Queued → Preflight → Decode → Analyze → Plan → AI Cleanup → Render → Limit/Safety → QC → Tagging → Done
const STEP_LABELS = {
  preflight: 'Preflight (Datei prüfen)',
  decode: 'Decode (Input bereit)',
  analyze: 'Audioanalyse',
  plan: 'Mastering-Plan (KI)',
  ai_cleanup: 'Vorverarbeitung (AI Cleanup)',
  render: 'Loudness & Limiter',
  limit_safety: 'Limit/Safety',
  qc: 'Qualitätskontrolle (LUFS / True Peak)',
  tagging: 'Tagging & Report',
  // Legacy
  analyzing: 'Audioanalyse',
  mastering: 'Mastering-Plan (KI)',
  rendering: 'Loudness & Limiter',
  finalizing: 'Abschluss',
}

const PIPELINE_STEPS = [
  { id: 'upload', label: 'Upload' },
  { id: 'queued', label: 'Queued' },
  { id: 'preflight', label: 'Preflight' },
  { id: 'decode', label: 'Decode' },
  { id: 'analyze', label: 'Analyze' },
  { id: 'plan', label: 'Plan' },
  { id: 'ai_cleanup', label: 'AI Cleanup' },
  { id: 'render', label: 'Render' },
  { id: 'limit_safety', label: 'Limit/Safety' },
  { id: 'qc', label: 'QC' },
  { id: 'tagging', label: 'Tagging' },
  { id: 'done', label: 'Done' },
]

const STEP_ORDER = {
  uploaded: 1, queued: 1,
  preflight: 2, decode: 3, analyze: 4, plan: 5, ai_cleanup: 6, render: 7, limit_safety: 8, qc: 9, tagging: 10,
  analyzing: 4, mastering: 5, rendering: 7, finalizing: 10,
}

function estimatedSecondsRemaining(job) {
  if (!job || (job.status !== 'pending' && job.status !== 'running')) return null
  if (job.progress == null || job.progress <= 0 || job.progress >= 100) return null
  const started = job.started_at ? new Date(job.started_at).getTime() : null
  if (!started || Number.isNaN(started)) return null
  const elapsedSec = (Date.now() - started) / 1000
  return Math.max(0, Math.round((elapsedSec * (100 - job.progress)) / job.progress))
}

function formatRemaining(seconds) {
  if (seconds == null) return null
  if (seconds < 60) return `ca. ${Math.round(seconds)} Sek.`
  return `ca. ${Math.round(seconds / 60)} Min.`
}

function stepIndex(track, job) {
  if (!track) return 0
  if (track.status === 'completed') return PIPELINE_STEPS.length - 1
  const maxStep = PIPELINE_STEPS.length - 1
  if (track.status === 'failed') {
    const step = job?.step || 'qc'
    return Math.min(STEP_ORDER[step] ?? 5, maxStep)
  }
  const step = job?.step || track.status
  return Math.min(STEP_ORDER[step] ?? 2, maxStep)
}

export default function TrackDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const toast = useToast()
  const prefs = usePreferences()
  const [track, setTrack] = useState(null)
  const [loading, setLoading] = useState(true)
  const [remastering, setRemastering] = useState(false)
  const [requeueing, setRequeueing] = useState(false)
  const [processingElapsed, setProcessingElapsed] = useState(0)
  const processingStartRef = useRef(null)
  const isAdmin = user?.is_admin === true

  const [masteringProfile, setMasteringProfile] = useState('spotify_safe')
  const [thoroughMastering, setThoroughMastering] = useState(false)
  const [showCoverModal, setShowCoverModal] = useState(false)
  const [coverStep, setCoverStep] = useState('options') // options | generating | pick | typography
  const [coverTitle, setCoverTitle] = useState('')
  const [coverArtist, setCoverArtist] = useState('')
  const [coverGenre, setCoverGenre] = useState('')
  const [coverVibe, setCoverVibe] = useState('')
  const [coverPalette, setCoverPalette] = useState('')
  const [coverStyle, setCoverStyle] = useState('')
  const [coverSubject, setCoverSubject] = useState('')
  const [coverDesignPrompt, setCoverDesignPrompt] = useState('')
  const [coverUseEditPass, setCoverUseEditPass] = useState(false)
  const [coverCandidates, setCoverCandidates] = useState([])
  const [coverSelectedIndex, setCoverSelectedIndex] = useState(null)
  const [coverTypographyLayout, setCoverTypographyLayout] = useState('bottom-center')
  const [coverTypographyLabel, setCoverTypographyLabel] = useState('')
  const [coverSubmitting, setCoverSubmitting] = useState(false)
  const [coverJobId, setCoverJobId] = useState(null)
  const [coverCacheBust, setCoverCacheBust] = useState(null)
  const coverCanvasRef = useRef(null)
  const COVER_LAYOUTS = [
    { id: 'top-left', label: 'Oben links' },
    { id: 'top-center', label: 'Oben Mitte' },
    { id: 'top-right', label: 'Oben rechts' },
    { id: 'bottom-left', label: 'Unten links' },
    { id: 'bottom-center', label: 'Unten Mitte' },
    { id: 'bottom-right', label: 'Unten rechts' },
  ]
  const [showCanvasModal, setShowCanvasModal] = useState(false)
  const [canvasFormat, setCanvasFormat] = useState('image')
  const [canvasDesignPrompt, setCanvasDesignPrompt] = useState('')
  const [canvasSubmitting, setCanvasSubmitting] = useState(false)
  const [canvasJobId, setCanvasJobId] = useState(null)
  const [canvasVideoBlobUrl, setCanvasVideoBlobUrl] = useState(null)
  const canvasVideoBlobRef = useRef(null)
  const [creditPrices, setCreditPrices] = useState({ cover_cost_credits: 0, canvas_cost_credits: 0 })
  const [metadata, setMetadata] = useState(null)
  const [metaForm, setMetaForm] = useState({ title_display: '', artist: '', album: '', genre: '', year: '' })
  const [metadataSaving, setMetadataSaving] = useState(false)
  const [metadataCleaning, setMetadataCleaning] = useState(false)
  const [embedCoverLoading, setEmbedCoverLoading] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [suggestion, setSuggestion] = useState(null)

  const fetchTrack = () => {
    if (!id) return Promise.resolve()
    return api.get(`/tracks/${id}`).then((r) => setTrack(r.data)).catch(() => setTrack(null))
  }

  const fetchMetadata = () => {
    if (!id) return Promise.resolve()
    return api.get(`/tracks/${id}/metadata`).then((r) => setMetadata(r.data)).catch(() => setMetadata(null))
  }

  useEffect(() => {
    fetchTrack().finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    api.get('/me/credits').then((r) => {
      const d = r.data || {}
      setCreditPrices({
        cover_cost_credits: d.cover_cost_credits ?? 0,
        canvas_cost_credits: d.canvas_cost_credits ?? 0,
      })
    }).catch(() => {})
  }, [id])

  useEffect(() => {
    if (track?.thorough_mastering != null) setThoroughMastering(!!track.thorough_mastering)
  }, [track?.id, track?.thorough_mastering])

  useEffect(() => {
    if (track) fetchMetadata()
  }, [id, track?.title_display, track?.genre])

  useEffect(() => {
    if (metadata != null || (track && !STATUS_PROCESSING.includes(track.status))) {
      setMetaForm({
        title_display: metadata?.title_display ?? track?.title_display ?? '',
        artist: metadata?.artist ?? track?.artist_display ?? '',
        album: metadata?.album ?? track?.album ?? '',
        genre: metadata?.genre ?? track?.genre ?? '',
        year: metadata?.year != null ? String(metadata.year) : (track?.year != null ? String(track.year) : ''),
      })
    }
  }, [metadata, track?.title_display, track?.artist_display, track?.album, track?.genre, track?.year, track?.status])

  const isProcessingOrActiveJob = track && (STATUS_PROCESSING.includes(track.status) || (track.job && (track.job.status === 'pending' || track.job.status === 'running')))
  useEffect(() => {
    if (!isProcessingOrActiveJob) return
    if (!processingStartRef.current) processingStartRef.current = Date.now()
    const t = setInterval(() => {
      setProcessingElapsed(Math.floor((Date.now() - processingStartRef.current) / 1000))
    }, 1000)
    return () => clearInterval(t)
  }, [!!isProcessingOrActiveJob])

  useEffect(() => {
    if (!isProcessingOrActiveJob) return
    const poll = setInterval(() => fetchTrack(), POLL_INTERVAL_MS)
    return () => clearInterval(poll)
  }, [id, !!isProcessingOrActiveJob])

  useEffect(() => {
    if (track && !isProcessingOrActiveJob) {
      processingStartRef.current = null
    }
  }, [track?.status, track?.job?.status, !!isProcessingOrActiveJob])

  const isCoverJobRunning = track?.job?.job_type === 'cover' && ['pending', 'running'].includes(track?.job?.status)
  useEffect(() => {
    if (coverJobId && track?.job?.id === coverJobId && !['pending', 'running'].includes(track?.job?.status)) {
      setCoverJobId(null)
      setCoverCacheBust(Date.now())
      if (showCoverModal && id) {
        setCoverStep('pick')
        api.get(`/tracks/${id}/cover/candidates`).then((r) => {
          const list = r.data?.candidates ?? []
          setCoverCandidates(list)
        }).catch(() => setCoverCandidates([]))
      }
    }
  }, [coverJobId, track?.job?.id, track?.job?.status, showCoverModal, id])
  useEffect(() => {
    if (!coverJobId || !isCoverJobRunning || track?.job?.id !== coverJobId) return
    const poll = setInterval(() => fetchTrack(), POLL_INTERVAL_MS)
    return () => clearInterval(poll)
  }, [id, coverJobId, isCoverJobRunning, track?.job?.id, track?.job?.status])

  const isCanvasJobRunning = track?.job?.job_type === 'canvas' && ['pending', 'running'].includes(track?.job?.status)
  useEffect(() => {
    if (canvasJobId && track?.job?.id === canvasJobId && !['pending', 'running'].includes(track?.job?.status)) {
      setCanvasJobId(null)
    }
  }, [canvasJobId, track?.job?.id, track?.job?.status])
  useEffect(() => {
    if (!canvasJobId || !isCanvasJobRunning || track?.job?.id !== canvasJobId) return
    const poll = setInterval(() => fetchTrack(), POLL_INTERVAL_MS)
    return () => clearInterval(poll)
  }, [id, canvasJobId, isCanvasJobRunning, track?.job?.id, track?.job?.status])

  // Canvas als Video: per Fetch mit Token laden, Blob-URL für zuverlässige Wiedergabe
  useEffect(() => {
    const isMp4 = track?.canvas_path && String(track.canvas_path).toLowerCase().endsWith('.mp4')
    if (!id || !track?.has_canvas_download || !isMp4) {
      if (canvasVideoBlobRef.current) {
        URL.revokeObjectURL(canvasVideoBlobRef.current)
        canvasVideoBlobRef.current = null
      }
      setCanvasVideoBlobUrl(null)
      return
    }
    let cancelled = false
    const url = downloadUrl(id, 'canvas')
    fetch(url)
      .then((res) => {
        if (!res.ok || cancelled) return null
        return res.blob()
      })
      .then((blob) => {
        if (!blob || cancelled) return
        if (canvasVideoBlobRef.current) URL.revokeObjectURL(canvasVideoBlobRef.current)
        const blobUrl = URL.createObjectURL(blob)
        canvasVideoBlobRef.current = blobUrl
        setCanvasVideoBlobUrl(blobUrl)
      })
      .catch(() => {
        if (!cancelled) setCanvasVideoBlobUrl(null)
      })
    return () => {
      cancelled = true
      if (canvasVideoBlobRef.current) {
        URL.revokeObjectURL(canvasVideoBlobRef.current)
        canvasVideoBlobRef.current = null
      }
      setCanvasVideoBlobUrl(null)
    }
  }, [id, track?.has_canvas_download, track?.canvas_path])

  const startMastering = async () => {
    setRemastering(true)
    try {
      await api.post(`/master/${id}`, {
        target_profile: masteringProfile,
        language: prefs.language || undefined,
        thorough_mastering: thoroughMastering,
      })
      processingStartRef.current = Date.now()
      setProcessingElapsed(0)
      await fetchTrack()
      toast('Mastering gestartet', 'success')
    } catch (e) {
      toast(e.message || 'Fehler', 'error')
    } finally {
      setRemastering(false)
    }
  }

  const openCoverModal = () => {
    setCoverStep('options')
    // Künstler nur aus Track/Metadaten – kein Branding/Admin vorbelegen
    setCoverTitle(track?.title_display || metaForm?.title_display || '')
    setCoverArtist(metaForm?.artist || track?.artist_display || '')
    setCoverGenre(track?.genre || '')
    setCoverSubject('')
    setCoverDesignPrompt(track?.cover_prompt || '')
    setCoverCandidates([])
    setCoverSelectedIndex(null)
    setShowCoverModal(true)
  }

  const closeCoverModal = () => {
    setShowCoverModal(false)
    setCoverStep('options')
    setCoverCandidates([])
  }

  const fetchCoverCandidates = () => {
    if (!id) return
    api.get(`/tracks/${id}/cover/candidates`).then((r) => {
      const list = r.data?.candidates ?? []
      setCoverCandidates(list)
      if (list.length) setCoverStep('pick')
    }).catch(() => setCoverCandidates([]))
  }

  const selectCoverCandidate = async (index) => {
    setCoverSubmitting(true)
    try {
      await api.post(`/tracks/${id}/cover/select`, { candidate_index: index })
      toast('Cover übernommen', 'success')
      await fetchTrack()
      setCoverCacheBust(Date.now())
      setCoverStep('typography')
    } catch (e) {
      toast(e?.message || 'Fehler', 'error')
    } finally {
      setCoverSubmitting(false)
    }
  }

  const downloadWithTypography = () => {
    const canvas = coverCanvasRef.current
    if (!canvas || !track?.has_cover_download || !id) return
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const pad = Math.min(img.width, img.height) * 0.08
      const isTop = coverTypographyLayout.startsWith('top')
      const isLeft = coverTypographyLayout.includes('left')
      const isRight = coverTypographyLayout.includes('right')
      let x = isLeft ? pad : isRight ? img.width - pad : img.width / 2
      let y = isTop ? pad : img.height - pad
      ctx.textAlign = isLeft ? 'left' : isRight ? 'right' : 'center'
      ctx.textBaseline = isTop ? 'top' : 'bottom'
      ctx.fillStyle = 'rgba(255,255,255,0.95)'
      ctx.font = `700 ${Math.round(img.width / 12)}px "Bebas Neue", sans-serif`
      ctx.fillText(coverTitle || 'Titel', x, y)
      y += Math.round(img.width / 18)
      ctx.font = `${Math.round(img.width / 20)}px Montserrat, sans-serif`
      ctx.fillStyle = 'rgba(255,255,255,0.85)'
      ctx.fillText(coverArtist || 'Künstler', x, y)
      if (coverTypographyLabel) {
        y += Math.round(img.width / 22)
        ctx.font = `${Math.round(img.width / 28)}px Inter, sans-serif`
        ctx.fillStyle = 'rgba(255,255,255,0.7)'
        ctx.fillText(coverTypographyLabel, x, y)
      }
      const link = document.createElement('a')
      link.download = `${coverTitle || 'cover'}_cover.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
      toast('Download gestartet', 'success')
    }
    img.onerror = () => toast('Bild konnte nicht geladen werden', 'error')
    img.src = coverImageUrl || downloadUrl(id, 'cover')
  }

  const openCanvasModal = () => {
    setCanvasFormat('image')
    setCanvasDesignPrompt('')
    setShowCanvasModal(true)
  }
  const closeCanvasModal = () => {
    setShowCanvasModal(false)
    setCanvasDesignPrompt('')
  }
  const startCanvas = async () => {
    setCanvasSubmitting(true)
    try {
      const body = { format: canvasFormat }
      if (canvasDesignPrompt.trim()) body.design_prompt = canvasDesignPrompt.trim()
      const { data } = await api.post(`/tracks/${id}/canvas`, body)
      setCanvasJobId(data.job_id)
      closeCanvasModal()
      await fetchTrack()
      toast('Canvas-Generierung gestartet', 'success')
    } catch (e) {
      toast(e.message || 'Fehler', 'error')
    } finally {
      setCanvasSubmitting(false)
    }
  }

  const startCover = async () => {
    setCoverSubmitting(true)
    try {
      const body = { with_text: false }
      if (coverTitle.trim()) body.title = coverTitle.trim()
      if (coverArtist.trim()) body.artist = coverArtist.trim()
      if (coverGenre.trim()) body.genre = coverGenre.trim()
      if (coverVibe.trim()) body.vibe = coverVibe.trim()
      if (coverPalette.trim()) body.palette = coverPalette.trim()
      if (coverStyle.trim()) body.style = coverStyle.trim()
      if (coverSubject.trim()) body.subject = coverSubject.trim()
      if (coverDesignPrompt.trim()) body.design_prompt = coverDesignPrompt.trim()
      body.use_edit_pass = coverUseEditPass
      const { data } = await api.post(`/tracks/${id}/cover`, body)
      setCoverJobId(data.job_id)
      setCoverStep('generating')
      await fetchTrack()
      toast('Cover-Generierung gestartet', 'success')
    } catch (e) {
      toast(e?.message || 'Fehler', 'error')
    } finally {
      setCoverSubmitting(false)
    }
  }

  const isReady = !loading && track
  const statusLabel = { uploaded: 'Hochgeladen', analyzing: 'Analyse', mastering: 'Mastering', completed: 'Abgeschlossen', failed: 'Nicht bestanden' }
  const job = isReady ? track.job : null
  const hasActiveJob = job && (job.status === 'pending' || job.status === 'running')
  const jobType = job?.job_type ?? 'full_pipeline'
  const hasActiveMasteringJob = hasActiveJob && (jobType === 'full_pipeline')
  const isProcessing = isReady && (STATUS_PROCESSING.includes(track.status) || hasActiveMasteringJob)
  const isCoverOrCanvasRunning = hasActiveJob && (jobType === 'cover' || jobType === 'canvas')
  const render = isReady ? (track.render || {}) : {}
  const hasMaster = isReady && (track.has_wav_download || track.has_mp3_download)
  const masterBaseName = isReady ? (track.filename_safe || track.original_filename || 'file').replace(/\.[^.]+$/, '') + ' AIM' : ''
  const lyrics = isReady ? (track.lyrics ?? '') : ''
  const currentStepIndex = stepIndex(track, job)
  const coverImageUrl = id ? `${downloadUrl(id, 'cover')}${coverCacheBust ? `&v=${coverCacheBust}` : ''}` : ''

  const requeueMastering = async () => {
    if (!isAdmin) return
    setRequeueing(true)
    try {
      await api.post(`/master/${id}/requeue`, {
        language: prefs.language || undefined,
      })
      await fetchTrack()
      processingStartRef.current = Date.now()
      setProcessingElapsed(0)
      toast('Job erneut eingereiht', 'success')
    } catch (e) {
      toast(e.message || 'Requeue fehlgeschlagen', 'error')
    } finally {
      setRequeueing(false)
    }
  }

  const saveMetadata = async (body) => {
    setMetadataSaving(true)
    try {
      const { data } = await api.put(`/tracks/${id}/metadata`, body)
      if (data) {
        setMetadata(data)
        setMetaForm({
          title_display: data.title_display ?? '',
          artist: data.artist ?? '',
          album: data.album ?? '',
          genre: data.genre ?? '',
          year: data.year != null ? String(data.year) : '',
        })
      }
      await fetchMetadata()
      await fetchTrack()
      toast('Metadaten gespeichert', 'success')
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Speichern fehlgeschlagen', 'error')
    } finally {
      setMetadataSaving(false)
    }
  }

  const saveAllMetadata = () => {
    const body = {
      title_display: metaForm.title_display.trim() || null,
      artist: metaForm.artist.trim() || null,
      album: metaForm.album.trim() || null,
      genre: metaForm.genre.trim() || null,
      year: metaForm.year.trim() ? parseInt(metaForm.year, 10) : null,
    }
    if (Number.isNaN(body.year)) body.year = null
    saveMetadata(body)
  }

  const runAutoClean = async () => {
    setMetadataCleaning(true)
    try {
      await api.post(`/tracks/${id}/metadata/clean`)
      await fetchMetadata()
      await fetchTrack()
      toast('Titel bereinigt', 'success')
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Bereinigung fehlgeschlagen', 'error')
    } finally {
      setMetadataCleaning(false)
    }
  }

  const runSuggest = async (apply = false) => {
    setSuggesting(true)
    setSuggestion(null)
    try {
      const r = await api.post(`/tracks/${id}/metadata/suggest`, { apply: !!apply })
      setSuggestion(r.data.suggestion)
      if (apply) {
        await fetchMetadata()
        await fetchTrack()
        toast('Vorschlag übernommen', 'success')
      }
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Vorschlag fehlgeschlagen', 'error')
    } finally {
      setSuggesting(false)
    }
  }

  const runEmbedCover = async () => {
    setEmbedCoverLoading(true)
    try {
      const { data } = await api.post(`/tracks/${id}/metadata/embed-cover`)
      toast('Cover wird eingebettet…', 'success')
      await fetchTrack()
      // Poll bis Embed-Job fertig, damit Nutzer erst danach herunterlädt
      const jobId = data?.job_id
      const maxWait = 30000
      const interval = 1500
      const start = Date.now()
      const poll = async () => {
        if (Date.now() - start > maxWait) {
          toast('Einbetten dauert länger. Bitte in Kürze erneut prüfen.', 'info')
          return
        }
        const res = await api.get(`/tracks/${id}`).catch(() => null)
        await fetchTrack()
        const job = res?.data?.job
        const isEmbedJob = job?.job_type === 'embed_cover' && job?.id === jobId
        if (!isEmbedJob) return
        if (job.status === 'completed') {
          toast('Cover in WAV und MP3 eingebettet.', 'success')
          return
        }
        if (job.status === 'failed') {
          const msg = job.result_message || 'Einbetten fehlgeschlagen'
          toast(msg, 'error')
          return
        }
        setTimeout(poll, interval)
      }
      setTimeout(poll, interval)
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Einbetten fehlgeschlagen', 'error')
    } finally {
      setEmbedCoverLoading(false)
    }
  }

  const applySuggestion = async () => {
    if (!suggestion) return
    setSuggesting(true)
    try {
      await api.post(`/tracks/${id}/metadata/suggest`, {
        apply: true,
        suggested_title_display: suggestion.suggested_title_display,
        suggested_title_clean: suggestion.suggested_title_clean,
        suggested_filename_safe: suggestion.suggested_filename_safe,
        confidence: suggestion.confidence,
        reason: suggestion.reason,
      })
      await fetchMetadata()
      await fetchTrack()
      setSuggestion(null)
      toast('Vorschlag übernommen', 'success')
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Übernehmen fehlgeschlagen', 'error')
    } finally {
      setSuggesting(false)
    }
  }

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
      toast('In Zwischenablage kopiert', 'success')
    }
  }

  const downloadLyricsTxt = () => {
    if (!track) return
    const name = (track.filename_safe || track.original_filename || 'lyrics').replace(/\.[^.]+$/, '') + ' AIM_lyrics.txt'
    const blob = new Blob([lyrics], { type: 'text/plain;charset=utf-8' })
    const u = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = u
    a.download = name
    a.click()
    URL.revokeObjectURL(u)
    toast('Lyrics heruntergeladen', 'success')
  }

  if (!isReady) {
    return (
      <PageShell>
        {loading ? <p className="text-white/70">Laden…</p> : <p className="text-white/70">Track nicht gefunden.</p>}
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="space-y-8">
        {/* Hero */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <Link to="/dashboard" className="text-sm text-white/60 hover:text-white">← Meine Masters</Link>
          <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-start">
            {track.has_cover_download && (
              <div className="h-[120px] w-[120px] shrink-0 overflow-hidden rounded-xl border border-white/10 bg-white/5">
                <img src={coverImageUrl} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" onError={(e) => { e.target.style.display = 'none' }} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-white">
                {(track.title_display || track.original_filename) + (hasMaster ? ' AIM' : '')}
              </h1>
              {track.artist_display && <p className="mt-1 text-white/60">{track.artist_display}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant={track.status}>{statusLabel[track.status] || track.status}</Badge>
                {track.genre && (
                  <span className="flex items-center gap-1.5">
                    <span className="text-xs text-white/50">Genre:</span>
                    <Badge variant={getGenreBadgeVariant(track.genre)}>{track.genre}</Badge>
                  </span>
                )}
                {track.is_explicit && <Badge variant="pending">Explicit</Badge>}
              </div>
            </div>
          </div>
        </div>

        {track.error_message && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-200">
            {track.error_message}
          </div>
        )}

        {(isProcessing || track.status === 'failed') && (
          <Card className={`border-white/10 ${track.status === 'failed' ? 'border-red-500/30' : ''}`}>
            <SectionTitle className="mb-4">
              Verarbeitungsstatus
              {track.status === 'failed' && (
                <span className="ml-2 text-sm font-normal text-red-400">· Fehlgeschlagen bei {PIPELINE_STEPS[currentStepIndex]?.label ?? 'QC'}</span>
              )}
            </SectionTitle>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {PIPELINE_STEPS.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        i < currentStepIndex
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : i === currentStepIndex
                            ? track.status === 'failed'
                              ? 'bg-red-500/30 text-red-200'
                              : 'bg-violet-500/30 text-violet-200'
                            : 'bg-white/5 text-white/50'
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < PIPELINE_STEPS.length - 1 && (
                      <span className="text-white/30">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {track.status === 'failed' && track.error_message && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {track.error_message}
                </div>
              )}
              <div>
                <ProgressBar
                  progress={job?.progress ?? 0}
                  variant={track.status === 'failed' ? 'error' : 'default'}
                  showLabel={true}
                  live={isProcessing && track.status !== 'failed'}
                />
                <div className="mt-2 space-y-0.5">
                  <p className="text-sm text-white/80">
                    <span className="font-medium text-violet-300">
                      {job?.step ? (STEP_LABELS[job.step] || job.step) : (track.status === 'uploaded' ? 'Wird in die Warteschlange gestellt…' : 'Läuft…')}
                    </span>
                    {' · '}
                    <span className="text-white/70">Datei: {track?.original_filename || track?.title_display || '–'}</span>
                  </p>
                  {(job?.step === 'qc' || job?.step === 'limit_safety') && (
                    <p className="text-xs text-white/50">
                      Qualitätskontrolle nach EBU R128 / Streaming-Standards. Bei langen Tracks 1–3 Min. – Fortschritt aktualisiert sich automatisch.
                    </p>
                  )}
                </div>
              </div>
              {isProcessing && (
                <p className="mt-1 text-sm text-white/60">
                  Laufzeit: {processingElapsed}s
                  {estimatedSecondsRemaining(job) != null && (
                    <span className="ml-2 font-medium text-violet-300">Noch {formatRemaining(estimatedSecondsRemaining(job))}</span>
                  )}
                  {track.status === 'uploaded' && !job?.step && estimatedSecondsRemaining(job) == null && ' · Wird in die Warteschlange gestellt…'}
                </p>
              )}
              {track.status === 'failed' && isAdmin && (
                <div className="flex flex-wrap items-center gap-3">
                  <PrimaryButton onClick={requeueMastering} disabled={requeueing}>
                    {requeueing ? 'Wird eingereiht…' : 'Mastering wiederholen'}
                  </PrimaryButton>
                  <span className="text-xs text-white/50">Erneut in die Warteschlange stellen und von vorn starten.</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {isCoverOrCanvasRunning && (
          <Card className="border-violet-500/20 bg-violet-500/5">
            <SectionTitle className="mb-2">
              {job?.job_type === 'cover' ? 'Cover wird erstellt' : 'Canvas wird erstellt'}
            </SectionTitle>
            <div className="space-y-2">
              <ProgressBar progress={job?.progress ?? 0} showLabel live />
              <p className="text-sm text-white/70">
                {estimatedSecondsRemaining(job) != null
                  ? `Noch ca. ${formatRemaining(estimatedSecondsRemaining(job))}`
                  : 'Bitte kurz warten – Fortschritt aktualisiert sich.'}
              </p>
            </div>
          </Card>
        )}

        {/* Waveform */}
        <Card>
          <SectionTitle className="mb-4">Waveform</SectionTitle>
          <div className="min-h-[160px]">
            <Waveform
              audioUrl={hasMaster ? downloadUrl(id, 'wav') : downloadUrl(id, 'original')}
              height={160}
              interact={true}
            />
          </div>
        </Card>

        <Card>
          <SectionTitle className="mb-4">AI Cleanup</SectionTitle>
          <p className="mb-2 text-sm text-white/70">
            Reduziert KI-Artefakte und Rauschen im Audio vor dem Mastering.
          </p>
          {isProcessing && track?.ai_cleanup_enabled !== false && (
            <p className="mb-3 rounded-lg bg-violet-500/20 px-3 py-2 text-sm text-violet-200">
              <strong>AI Cleanup läuft</strong> in diesem Durchlauf (siehe Fortschrittsbalken oben: Schritt „AI Cleanup“).
            </p>
          )}
          {!isProcessing && (
            <p className="mb-4 text-xs text-white/50">
              Beim Upload „AI Cleanup aktiv“ setzen oder hier aktivieren. Mit dem Button unten startest du die Pipeline inkl. AI Cleanup (Master wird neu erstellt).
            </p>
          )}
          <div className="mb-4 flex flex-wrap items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-white/90">
              <input
                type="checkbox"
                checked={track.ai_cleanup_enabled !== false}
                onChange={(e) => {
                  const v = e.target.checked
                  api.patch(`/tracks/${id}`, { ai_cleanup_enabled: v }).then(() => fetchTrack()).catch((err) => toast(err.message || 'Speichern fehlgeschlagen', 'error'))
                }}
                disabled={isProcessing}
                className="rounded"
              />
              AI Cleanup aktiv
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/50">Stärke</span>
              <select
                value={track.ai_cleanup_strength || 'medium'}
                onChange={(e) => {
                  const v = e.target.value
                  api.patch(`/tracks/${id}`, { ai_cleanup_strength: v }).then(() => fetchTrack()).catch((err) => toast(err.message || 'Speichern fehlgeschlagen', 'error'))
                }}
                disabled={isProcessing}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white disabled:opacity-60"
              >
                <option value="low">Niedrig</option>
                <option value="medium">Mittel</option>
                <option value="high">Hoch</option>
              </select>
            </div>
          </div>
          {!isProcessing && hasMaster && (
            <PrimaryButton onClick={startMastering} disabled={remastering}>
              {remastering ? 'Wird eingereiht…' : 'Mit AI Cleanup neu masteren'}
            </PrimaryButton>
          )}
        </Card>

        {!isProcessing && (
          <Card>
            <SectionTitle className="mb-4">Metadaten bearbeiten</SectionTitle>
            {metadata?.needs_suggestion && (
              <p className="mb-3 text-sm text-amber-200/90">Titel wirkt generisch (z. B. „Untitled“, „Track 01“). Auto-clean oder „Mit KI vorschlagen“ nutzen.</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-white/50">Titel (Anzeige)</label>
                <input
                  type="text"
                  value={metaForm.title_display}
                  onChange={(e) => setMetaForm((f) => ({ ...f, title_display: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value.trim()
                    const prev = metadata?.title_display ?? track.title_display ?? ''
                    if (v !== prev) saveMetadata({ title_display: v || null })
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                  placeholder="Titel"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Künstler</label>
                <input
                  type="text"
                  value={metaForm.artist}
                  onChange={(e) => setMetaForm((f) => ({ ...f, artist: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value.trim()
                    const prev = metadata?.artist ?? track.artist_display ?? ''
                    if (v !== prev) saveMetadata({ artist: v || null })
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                  placeholder="Künstler"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Album</label>
                <input
                  type="text"
                  value={metaForm.album}
                  onChange={(e) => setMetaForm((f) => ({ ...f, album: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value.trim()
                    const prev = metadata?.album ?? track.album ?? ''
                    if (v !== prev) saveMetadata({ album: v || null })
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                  placeholder="Album"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Genre</label>
                <input
                  type="text"
                  value={metaForm.genre}
                  onChange={(e) => setMetaForm((f) => ({ ...f, genre: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value.trim()
                    const prev = metadata?.genre ?? track.genre ?? ''
                    if (v !== prev) saveMetadata({ genre: v || null, final_genre: v || null })
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                  placeholder="Genre"
                />
                {(track.genre_label != null || track.genre_reason != null) && (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70">
                    <span className="text-white/50">Erkannt: </span>
                    <span className="font-medium text-white/90">{track.genre_label ?? '–'}</span>
                    {track.genre_confidence != null && (
                      <span className="ml-1.5 text-white/50">({Math.round((track.genre_confidence ?? 0) * 100)}%)</span>
                    )}
                    {track.genre_reason && (
                      <p className="mt-1 text-white/50">{track.genre_reason}</p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Jahr</label>
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  value={metaForm.year}
                  onChange={(e) => setMetaForm((f) => ({ ...f, year: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value ? parseInt(e.target.value, 10) : null
                    if (v !== (metadata?.year ?? track.year)) saveMetadata({ year: v })
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30"
                  placeholder="Jahr"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs text-white/50">Dateiname (sicher)</label>
                <p className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/70">{metadata?.filename_safe ?? track.filename_safe ?? '–'}</p>
              </div>
              <div className="sm:col-span-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-white/90">
                  <input
                    type="checkbox"
                    checked={!!(metadata?.ai_generated_disclosure ?? track?.ai_generated_disclosure)}
                    onChange={(e) => saveMetadata({ ai_generated_disclosure: e.target.checked })}
                    disabled={metadataSaving}
                    className="rounded"
                  />
                  KI-Offenlegung in Datei schreiben (z. B. für Amuse/Spotify)
                </label>
                <p className="mt-1 text-xs text-white/50">Standard: aus. Es wird dann kein AI-Hinweis in die Master-Dateien geschrieben – Distributoren können es nicht aus den Metadaten auslesen. Nur aktivieren, wenn du explizit offenlegen willst.</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <PrimaryButton onClick={saveAllMetadata} disabled={metadataSaving}>
                {metadataSaving ? 'Speichern…' : 'Metadaten speichern'}
              </PrimaryButton>
              <SecondaryButton onClick={runAutoClean} disabled={metadataCleaning}>
                {metadataCleaning ? 'Bereinigen…' : 'Auto-clean'}
              </SecondaryButton>
              <SecondaryButton onClick={() => runSuggest(false)} disabled={suggesting}>
                {suggesting ? 'Vorschlag…' : 'Mit KI vorschlagen'}
              </SecondaryButton>
              <span className="flex flex-wrap items-center gap-2">
                <SecondaryButton
                  onClick={runEmbedCover}
                  disabled={
                    embedCoverLoading ||
                    !track?.has_cover_download ||
                    (!track?.has_wav_download && !track?.has_mp3_download) ||
                    (track?.job?.status === 'pending' && track?.job?.job_type === 'embed_cover') ||
                    (track?.job?.status === 'running' && track?.job?.job_type === 'embed_cover')
                  }
                  title={!track?.has_cover_download ? 'Zuerst ein Cover erzeugen (z. B. unter „Cover“).' : (track?.has_wav_download || track?.has_mp3_download) ? 'Cover in WAV- und MP3-Metadaten einbetten.' : 'Keine Master-Dateien (WAV/MP3) vorhanden.'}
                >
                  {embedCoverLoading || (track?.job?.job_type === 'embed_cover' && (track?.job?.status === 'pending' || track?.job?.status === 'running')) ? 'Wird eingebettet…' : 'Cover in WAV/MP3 einbetten'}
                </SecondaryButton>
                {(!track?.has_cover_download || (!track?.has_wav_download && !track?.has_mp3_download)) && (
                  <span className="text-xs text-white/50">Nur wenn Cover und Master (WAV/MP3) vorhanden.</span>
                )}
              </span>
              {suggestion && (
                <>
                  <span className="text-sm text-white/70">{suggestion.suggested_title_display}</span>
                  <PrimaryButton onClick={applySuggestion} disabled={suggesting}>Übernehmen</PrimaryButton>
                </>
              )}
            </div>
          </Card>
        )}

        {(track.status === 'failed' || track.status === 'completed') && (
          <Card>
            <SectionTitle className="mb-4">
              {track.status === 'failed' ? 'Mastering erneut starten' : 'Mastering erneut ausführen'}
            </SectionTitle>
            <p className="mb-4 text-sm text-white/70">
              {track.status === 'failed'
                ? 'Ziel-Profil wählen (WAV 24 Bit + MP3 320 kbps).'
                : 'Pipeline erneut laufen lassen – z. B. um AI Cleanup (oben) zu nutzen oder ein anderes Profil.'}
            </p>
            <div className="mb-4 flex flex-wrap gap-4">
              {[
                { id: 'spotify_safe', label: 'Spotify Safe (-14 / -1.0)', title: 'Streaming-Baseline' },
                { id: 'spotify_optimized', label: 'Spotify Optimized (-10 / -1.0)', title: 'Lauter, modern' },
                { id: 'radio', label: 'Radio (-11 / -1.2)', title: 'Broadcast' },
                { id: 'club_modern', label: 'Club Modern (-9 / -1.0)', title: 'DJ-freundlich' },
                { id: 'club_punch', label: 'Club Punch (-8 / -0.6) ★', title: 'Empfohlen für DJs' },
                { id: 'dj_set', label: 'DJ Set (-9.5 / -0.8)', title: 'Long-Play' },
                { id: 'archive', label: 'Archive (-16 / -1.0)', title: 'Max. Qualität' },
                { id: 'club', label: 'Club (Legacy)', title: 'Legacy (may sound flat)' },
                { id: 'tidal_hifi', label: 'Tidal HiFi (-14 / -1.0)', title: 'Hi-Fi Streaming' },
                { id: 'apple_music', label: 'Apple Music (-16 / -1.0)', title: 'Konservativ' },
                { id: 'youtube', label: 'YouTube (-14 / -1.0)', title: 'Video-optimiert' },
              ].map(({ id, label, title }) => (
                <label key={id} className="flex cursor-pointer items-center gap-2 text-sm text-white/90" title={title || undefined}>
                  <input
                    type="radio"
                    name="profile"
                    value={id}
                    checked={masteringProfile === id}
                    onChange={() => setMasteringProfile(id)}
                    className="rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
            <label className="mb-4 flex cursor-pointer items-start gap-3 text-sm text-white/90">
              <input
                type="checkbox"
                checked={thoroughMastering}
                onChange={(e) => setThoroughMastering(e.target.checked)}
                className="mt-1 rounded"
              />
              <span>Gründlicher Durchlauf (mehr Headroom, etwas bessere Qualität)</span>
            </label>
            <PrimaryButton onClick={startMastering} disabled={remastering}>
              {remastering ? 'Wird eingereiht…' : (track.status === 'failed' ? 'Mastering starten' : 'Mastering erneut ausführen')}
            </PrimaryButton>
          </Card>
        )}

        {!isProcessing && hasMaster && (
          <Card>
            <SectionTitle className="mb-4">
              {track.has_cover_download ? 'Cover neu generieren' : 'Cover generieren'}
            </SectionTitle>
            <p className="mb-4 text-sm text-white/70">
              {track.has_cover_download
                ? 'Neues Cover erstellen. Titel und Künstler werden professionell ins Bild integriert (ein Cover wird erzeugt).'
                : 'Ein Cover mit Titel und Künstler im Bild erstellen. Genre-basiertes Design, ein hochwertiges Cover.'}
            </p>
            {isCoverJobRunning ? (
              <p className="text-sm text-white/60">Cover wird generiert…</p>
            ) : (
              <PrimaryButton onClick={openCoverModal}>
                {track.has_cover_download ? 'Cover neu generieren' : 'Cover generieren'}
                {creditPrices.cover_cost_credits > 0 ? ` (${creditPrices.cover_cost_credits} Credit${creditPrices.cover_cost_credits !== 1 ? 's' : ''})` : ' (kostenlos)'}
              </PrimaryButton>
            )}
          </Card>
        )}

        {!isProcessing && hasMaster && (
          <Card>
            <SectionTitle className="mb-4">
              {track.has_canvas_download ? 'Spotify-Canvas neu generieren' : 'Spotify-Canvas generieren'}
            </SectionTitle>
            <p className="mb-4 text-sm text-white/70">
              Vertikales Bild oder kurzes Video (9:16), passend zu Titel und Song – z. B. für Spotify Canvas. Optional eigener Design-Prompt.
            </p>
            {isCanvasJobRunning ? (
              <p className="text-sm text-white/60">Canvas wird generiert…</p>
            ) : (
              <PrimaryButton onClick={openCanvasModal}>
                {track.has_canvas_download ? 'Canvas neu generieren' : 'Canvas generieren'}
                {creditPrices.canvas_cost_credits > 0 ? ` (${creditPrices.canvas_cost_credits} Credit${creditPrices.canvas_cost_credits !== 1 ? 's' : ''})` : ' (kostenlos)'}
              </PrimaryButton>
            )}
          </Card>
        )}

        {!isProcessing && hasMaster && (
          <>
            {render.qc_lufs != null && track.mastering_plan && (
              <Card>
                <SectionTitle className="mb-3">Qualität</SectionTitle>
                {(() => {
                  const targetLufs = Number(track.mastering_plan.loudness_target_lufs) || -14
                  const ceiling = Number(track.mastering_plan.limiter_ceiling_db) || -1
                  const diff = Math.abs((Number(render.qc_lufs) || 0) - targetLufs)
                  // Peak-Toleranz 0.7 dB über Limit – minimale Überschreitungen (z. B. -0.42 bei -1 dB) gelten noch als ok → „Sehr gut“
                  const peakOk = render.qc_true_peak == null || Number(render.qc_true_peak) <= ceiling + 0.7
                  const peakVal = render.qc_true_peak != null ? Number(render.qc_true_peak).toFixed(2) : '–'
                  // Bei intaktem Peak immer „Sehr gut“ – Headroom ist erlaubt, Qualität darf nicht darunter angezeigt werden
                  let qualityLabel = peakOk ? 'Sehr gut' : 'Okay'
                  let reason = peakOk
                    ? `Lautheit ${render.qc_lufs} LUFS (Ziel ${targetLufs}${diff > 0.3 ? `, Abweichung ${diff.toFixed(2)} dB` : ''}). Geeignet für Streaming & Release.`
                    : `True Peak: ${peakVal} dB (Limit ${ceiling} dB). „Mastering erneut ausführen“ mit anderem Profil für bessere Werte.`
                  const lufsScore = Math.max(0, Math.min(100, 100 - diff * 20))
                  const peakScore = peakOk ? 100 : Math.max(0, Math.min(100, 100 - Math.max(0, (Number(render.qc_true_peak) - ceiling)) * 30))
                  const rawScore = Math.round((lufsScore + peakScore) / 2)
                  const aiQualityScore = peakOk ? Math.max(90, Math.min(100, rawScore)) : Math.max(0, Math.min(89, rawScore))
                  const qcGateLabel = peakOk ? 'QC PASS (WAV master)' : 'QC FAIL (WAV master)'
                  return (
                    <div>
                      <div className="mb-2 flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-white">{aiQualityScore}</span>
                        <span className="text-sm text-white/50">AI Quality Score (0–100)</span>
                      </div>
                      <Badge variant={qualityLabel === 'Sehr gut' ? 'ready' : qualityLabel === 'Gut' ? 'running' : 'default'} className="mb-2">
                        {qcGateLabel}
                      </Badge>
                      <p className="text-sm text-white/70">{reason}</p>
                    </div>
                  )
                })()}
              </Card>
            )}

            <Card>
              <SectionTitle className="mb-4">A/B-Vergleich – Vorher vs. Nachher</SectionTitle>
              <ABPlayer trackId={Number(id)} hasOriginal={true} hasMaster={track.has_wav_download} />
            </Card>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {track.analysis && (
                <>
                  <StatCard label="LUFS (Original)" value={track.analysis.lufs_integrated} />
                  <StatCard label="True Peak" value={track.analysis.true_peak_db != null ? `${track.analysis.true_peak_db} dB` : '–'} />
                  <StatCard label="Sample Rate" value={track.analysis.sample_rate != null ? `${track.analysis.sample_rate} Hz` : '–'} />
                  <StatCard label="Dauer" value={track.analysis.duration_seconds != null ? `${track.analysis.duration_seconds.toFixed(1)} s` : '–'} />
                </>
              )}
              {hasMaster && (render.qc_lufs != null || render.qc_true_peak != null) && (
                <>
                  {render.qc_lufs != null && <StatCard label="LUFS (WAV master)" value={render.qc_lufs} />}
                  {render.qc_true_peak != null && <StatCard label="True Peak (WAV master)" value={`${render.qc_true_peak} dB`} />}
                </>
              )}
              {hasMaster && (render.mp3_qc_lufs != null || render.mp3_qc_true_peak != null) && (
                <StatCard
                  label="MP3 QC (info)"
                  value={[render.mp3_qc_lufs != null ? `${render.mp3_qc_lufs} LUFS` : null, render.mp3_qc_true_peak != null ? `${render.mp3_qc_true_peak} dB TP` : null].filter(Boolean).join(', ') || '–'}
                />
              )}
            </div>

            {(track.analysis?.sample_rate != null || track.analysis?.bit_depth != null || hasMaster) && (
              <Card>
                <SectionTitle className="mb-3">Auflösung & Formate</SectionTitle>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <dt className="text-white/50">Upload</dt>
                  <dd className="text-white/90">
                    {track.analysis?.sample_rate != null ? `${track.analysis.sample_rate} Hz` : '–'}
                    {track.analysis?.bit_depth != null ? `, ${track.analysis.bit_depth} bit` : ''}
                    , Stereo
                  </dd>
                  <dt className="text-white/50">Export (Master)</dt>
                  <dd className="text-white/90">
                    WAV {track.analysis?.sample_rate ?? 44100} Hz, 24 bit · MP3 320 kbps
                  </dd>
                </dl>
              </Card>
            )}

            {track.mastering_plan && (
              <Card>
                <SectionTitle className="mb-4">Mastering-Plan</SectionTitle>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <dt className="text-white/50">Profil</dt>
                  <dd className="text-white/90">
                    {({
                      spotify_safe: 'Spotify Safe',
                      spotify_perfect: 'Spotify Safe',
                      spotify_optimized: 'Spotify Optimized',
                      tidal_hifi: 'Tidal HiFi',
                      apple_music: 'Apple Music',
                      youtube: 'YouTube',
                      radio: 'Radio',
                      club_modern: 'Club Modern',
                      club_punch: 'Club Punch',
                      dj_set: 'DJ Set',
                      archive: 'Archive',
                      club: 'Club (Legacy)',
                      club_loud: 'Club (Legacy)',
                      general: 'Spotify Safe',
                      festival: 'Club Punch',
                    }[track.mastering_plan.target_profile] || track.mastering_plan.target_profile)}
                  </dd>
                  <dt className="text-white/50">Loudness-Ziel</dt>
                  <dd className="text-white/90">{track.mastering_plan.loudness_target_lufs} LUFS</dd>
                  <dt className="text-white/50">Limiter</dt>
                  <dd className="text-white/90">{track.mastering_plan.limiter_ceiling_db} dB</dd>
                  {track.mastering_plan.notes && (
                    <>
                      <dt className="text-white/50">Hinweise</dt>
                      <dd className="text-white/90">{track.mastering_plan.notes}</dd>
                    </>
                  )}
                </dl>
              </Card>
            )}

            <Card>
              <SectionTitle className="mb-4">Master herunterladen</SectionTitle>
              <div className="flex flex-wrap gap-3">
                {track.has_wav_download && (
                  <a href={downloadUrl(id, 'wav')} download={`${masterBaseName}.wav`} rel="noopener noreferrer">
                    <PrimaryButton>WAV (24 Bit)</PrimaryButton>
                  </a>
                )}
                {track.has_mp3_download && (
                  <a href={downloadUrl(id, 'mp3')} download={`${masterBaseName}.mp3`} rel="noopener noreferrer">
                    <SecondaryButton>MP3 320 kbps</SecondaryButton>
                  </a>
                )}
                {track.has_report_download && (
                  <a href={downloadUrl(id, 'report')} download={`${masterBaseName}_report.txt`} rel="noopener noreferrer">
                    <SecondaryButton>Report (TXT)</SecondaryButton>
                  </a>
                )}
                {track.has_mp3_download && (
                  <a href={downloadUrl(id, 'azura')} download={`${masterBaseName}_azura.zip`} rel="noopener noreferrer">
                    <SecondaryButton>Für Azuracast (ZIP: MP3 + Cover + Metadaten)</SecondaryButton>
                  </a>
                )}
              </div>
            </Card>

            <Card padding="none">
              <div className="p-4">
                <AccordionSection title="Metadata (Genre, Explicit)" defaultOpen>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs text-white/50">Genre</span>
                      <p className="mt-0.5 text-white/90">{track.genre || '–'}</p>
                    </div>
                    {(track.genre_label != null || track.genre_reason != null) && (
                      <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                        <span className="text-xs text-white/50">Erkanntes Genre</span>
                        <p className="mt-0.5 font-medium text-white/90">{track.genre_label ?? '–'}</p>
                        {track.genre_confidence != null && (
                          <p className="mt-0.5 text-xs text-white/50">Konfidenz: {Math.round((track.genre_confidence ?? 0) * 100)}%</p>
                        )}
                        {track.genre_reason && (
                          <p className="mt-1 text-xs text-white/50">{track.genre_reason}</p>
                        )}
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-white/50">Status</span>
                      <p className="mt-0.5">
                        {track.is_explicit ? (
                          <Badge variant="pending">Explizit</Badge>
                        ) : (
                          <span className="text-white/70">Nicht explizit</span>
                        )}
                      </p>
                    </div>
                  </div>
                </AccordionSection>
                {track.suggested_titles && track.suggested_titles.length > 0 && (
                  <AccordionSection title="Titelvorschläge">
                    <ul className="space-y-2">
                      {track.suggested_titles.map((tit, idx) => (
                        <li key={idx} className="flex items-center justify-between gap-2">
                          <span className="text-white/90">{tit}</span>
                          <SecondaryButton onClick={() => copyToClipboard(tit)}>Kopieren</SecondaryButton>
                        </li>
                      ))}
                    </ul>
                  </AccordionSection>
                )}
                {track.has_cover_download && (
                  <AccordionSection title="Cover" defaultOpen>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="min-h-[220px] min-w-[220px] overflow-hidden rounded-xl border border-white/10 bg-white/5">
                        <img src={coverImageUrl} alt="Cover" className="h-full w-full object-cover" loading="lazy" decoding="async" onError={(e) => { e.target.style.display = 'none' }} />
                      </div>
                      <div>
                        <a href={coverImageUrl} download={`${masterBaseName}_cover.png`}>
                          <SecondaryButton>Cover herunterladen (PNG)</SecondaryButton>
                        </a>
                      </div>
                    </div>
                  </AccordionSection>
                )}
                {track.has_canvas_download && (
                  <AccordionSection title="Spotify-Canvas">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="min-h-[240px] min-w-[135px] max-w-[160px] overflow-hidden rounded-xl border border-white/10 bg-white/5 bg-black/20">
                        {track.canvas_path && String(track.canvas_path).toLowerCase().endsWith('.mp4') ? (
                          <video
                            key={canvasVideoBlobUrl || track.canvas_path}
                            src={canvasVideoBlobUrl || downloadUrl(id, 'canvas')}
                            className="h-full w-full object-cover"
                            muted
                            loop
                            playsInline
                            autoPlay
                            controls
                            preload="metadata"
                          />
                        ) : (
                          <img src={downloadUrl(id, 'canvas')} alt="Canvas" className="h-full w-full object-cover" loading="lazy" decoding="async" onError={(e) => { e.target.style.display = 'none' }} />
                        )}
                      </div>
                      <div>
                        <a href={downloadUrl(id, 'canvas')} download={`${masterBaseName}_canvas${track.canvas_path && String(track.canvas_path).toLowerCase().endsWith('.mp4') ? '.mp4' : '.png'}`}>
                          <SecondaryButton>Canvas herunterladen ({track.canvas_path && String(track.canvas_path).toLowerCase().endsWith('.mp4') ? 'MP4' : 'PNG'})</SecondaryButton>
                        </a>
                      </div>
                    </div>
                  </AccordionSection>
                )}
                <AccordionSection title="Songtext">
                  <p className="mb-2 text-xs text-white/50">
                    Aus Audio extrahiert (Whisper). Bei rein instrumentaler Musik wird „INSTRUMENTAL“ angezeigt.
                  </p>
                  <div className="space-y-3">
                    <textarea
                      readOnly
                      value={lyrics}
                      rows={8}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30 resize-none"
                      placeholder="Kein Songtext extrahiert."
                    />
                    <div className="flex flex-wrap gap-2">
                      <SecondaryButton onClick={() => copyToClipboard(lyrics)}>Kopieren</SecondaryButton>
                      <SecondaryButton onClick={downloadLyricsTxt} disabled={!lyrics}>Als TXT herunterladen</SecondaryButton>
                    </div>
                  </div>
                </AccordionSection>
              </div>
            </Card>
          </>
        )}

        {!isProcessing && track.analysis && !hasMaster && (
          <Card>
            <SectionTitle className="mb-4">Analyse (Original)</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="LUFS" value={track.analysis.lufs_integrated} />
              <StatCard label="True Peak" value={`${track.analysis.true_peak_db} dB`} />
              <StatCard label="RMS" value={`${track.analysis.rms_db} dB`} />
              <StatCard label="Crest Factor" value={track.analysis.crest_factor} />
              <StatCard label="Sample Rate" value={`${track.analysis.sample_rate} Hz`} />
              <StatCard label="Dauer" value={track.analysis.duration_seconds != null ? `${track.analysis.duration_seconds.toFixed(1)} s` : '–'} />
            </div>
          </Card>
        )}

        {showCoverModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" onClick={closeCoverModal}>
            <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-studio-bg p-6 shadow-xl my-8" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg font-semibold text-white">Cover generieren</h3>
              {coverStep === 'options' && (
                <>
                  <p className="mb-4 text-sm text-white/70">Titel und Künstler werden professionell ins Cover integriert. Es wird ein Cover erzeugt. Mit eigenem Prompt steuerst du die Bildidee; leer lassen: GPT erstellt die Idee aus Titel & Künstler.</p>
                  <div className="mb-4 space-y-3 max-h-[60vh] overflow-y-auto">
                    <div>
                      <label className="block text-sm font-medium text-white/80">Eigener Prompt (optional)</label>
                      <textarea value={coverDesignPrompt} onChange={(e) => setCoverDesignPrompt(e.target.value)} placeholder="Leer lassen: GPT erstellt die Idee aus Titel & Künstler" rows={2} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm mt-1 resize-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Titel & Künstler (werden ins Cover integriert)</label>
                      <div className="flex gap-2 mt-1">
                        <input type="text" value={coverTitle} onChange={(e) => setCoverTitle(e.target.value)} placeholder="Titel" className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm" />
                        <input type="text" value={coverArtist} onChange={(e) => setCoverArtist(e.target.value)} placeholder="Künstler" className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Genre</label>
                      <input type="text" value={coverGenre} onChange={(e) => setCoverGenre(e.target.value)} placeholder="z. B. electronic, hip-hop" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Vibe</label>
                      <input type="text" value={coverVibe} onChange={(e) => setCoverVibe(e.target.value)} placeholder="z. B. dark, euphoric" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Farbpalette</label>
                      <input type="text" value={coverPalette} onChange={(e) => setCoverPalette(e.target.value)} placeholder="z. B. muted earth tones, neon accent" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Stil</label>
                      <select value={coverStyle} onChange={(e) => setCoverStyle(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white text-sm mt-1">
                        <option value="">Automatisch</option>
                        <option value="cinematic photography">Cinematic Photography</option>
                        <option value="premium 3D product render">Premium 3D Render</option>
                        <option value="abstract graphic design (vector-like)">Abstract Graphic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80">Subject / Keywords</label>
                      <input type="text" value={coverSubject} onChange={(e) => setCoverSubject(e.target.value)} placeholder="z. B. abstract focal element, silhouette" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/40 text-sm mt-1" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={coverUseEditPass} onChange={(e) => setCoverUseEditPass(e.target.checked)} className="rounded border-white/20 text-violet-500" />
                      <span className="text-sm text-white/80">Optional: Edit-Pass (mehr Realismus)</span>
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <PrimaryButton onClick={startCover} disabled={coverSubmitting}>{coverSubmitting ? 'Starte…' : 'Cover generieren'}</PrimaryButton>
                    <SecondaryButton onClick={closeCoverModal}>Schließen</SecondaryButton>
                  </div>
                </>
              )}
              {coverStep === 'generating' && (
                <div className="py-8 text-center text-white/80">
                  <p className="mb-2">Cover wird generiert…</p>
                  <p className="text-sm text-white/50">Bitte warten, das kann 1–2 Min. dauern.</p>
                </div>
              )}
              {coverStep === 'pick' && (
                <>
                  <p className="mb-4 text-sm text-white/70">Das generierte Cover kannst du übernehmen. Danach kannst du die Typografie anpassen.</p>
                  {coverCandidates.length === 0 ? (
                    <p className="mb-4 text-white/50 text-sm">Keine Kandidaten geladen. Du kannst das aktuelle Cover nutzen und zur Typografie gehen.</p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {coverCandidates.map((idx) => (
                      <div key={idx} className={`rounded-xl overflow-hidden border-2 ${coverSelectedIndex === idx ? 'border-violet-500' : 'border-white/10'}`}>
                        <img src={downloadUrl(id, `cover_candidate_${idx}`)} alt={list.length === 1 ? 'Cover' : `Variante ${idx + 1}`} className="w-full aspect-square object-cover" loading="lazy" decoding="async" />
                        <button type="button" onClick={() => setCoverSelectedIndex(idx)} className="w-full py-2 text-sm text-white/80 hover:bg-white/10">Auswählen</button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <PrimaryButton onClick={() => coverSelectedIndex != null && selectCoverCandidate(coverSelectedIndex)} disabled={coverSelectedIndex == null || coverSubmitting}>
                      {coverSubmitting ? 'Übernehmen…' : 'Als Cover übernehmen'}
                    </PrimaryButton>
                    <SecondaryButton onClick={() => { fetchTrack(); setCoverStep('typography'); }}>Direkt zu Typografie</SecondaryButton>
                    <button type="button" onClick={closeCoverModal} className="text-sm text-white/50 hover:text-white/80">Schließen</button>
                  </div>
                </>
              )}
              {coverStep === 'typography' && (
                <>
                  <p className="mb-4 text-sm text-white/70">Titel und Künstler mit echten Schriften über das Cover legen. Safe Margins 5–8%.</p>
                  <div className="mb-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-white/80">Layout</label>
                      <select value={coverTypographyLayout} onChange={(e) => setCoverTypographyLayout(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white text-sm mt-1">
                        {COVER_LAYOUTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    </div>
                    <label className="block text-sm font-medium text-white/80">Optional: Label (z. B. DJ Mac)</label>
                    <input type="text" value={coverTypographyLabel} onChange={(e) => setCoverTypographyLabel(e.target.value)} placeholder="Label" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white text-sm mt-1" />
                  </div>
                  <div className="mb-4 rounded-xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: '1' }}>
                    <canvas ref={coverCanvasRef} width={600} height={600} className="hidden" />
                    <div className="relative w-full aspect-square bg-cover bg-center" style={{ backgroundImage: track?.has_cover_download ? `url(${coverImageUrl})` : undefined, backgroundColor: '#1a1a24' }}>
                      <div className="absolute inset-[8%] flex flex-col font-['Bebas_Neue'] text-white" style={{ fontFamily: 'Bebas Neue, sans-serif', justifyContent: coverTypographyLayout.startsWith('top') ? 'flex-start' : coverTypographyLayout.startsWith('bottom') ? 'flex-end' : 'center', alignItems: coverTypographyLayout.includes('left') ? 'flex-start' : coverTypographyLayout.includes('right') ? 'flex-end' : 'center' }}>
                        <span className="text-2xl sm:text-3xl leading-tight tracking-wide">{coverTitle || 'Titel'}</span>
                        <span className="text-lg sm:text-xl opacity-90 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>{coverArtist || 'Künstler'}</span>
                        {coverTypographyLabel && <span className="text-xs mt-2 opacity-70" style={{ fontFamily: 'Inter, sans-serif' }}>{coverTypographyLabel}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <PrimaryButton onClick={downloadWithTypography}>Mit Typografie herunterladen</PrimaryButton>
                    <SecondaryButton onClick={closeCoverModal}>Fertig</SecondaryButton>
                  </div>
                </>
              )}
              {(coverStep === 'options' || coverStep === 'pick' || coverStep === 'typography') && (
                <button type="button" onClick={closeCoverModal} className="mt-4 block text-sm text-white/50 hover:text-white/80">Schließen</button>
              )}
            </div>
          </div>
        )}

        {showCanvasModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeCanvasModal}>
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-studio-bg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg font-semibold text-white">Spotify-Canvas generieren</h3>
              <p className="mb-4 text-sm text-white/70">Vertikales Bild oder Video (9:16), passend zu Titel und Song. Optional eigenen Design-Prompt angeben.</p>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-white/80">Format</label>
                <div className="flex gap-3">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input type="radio" name="canvasFormat" value="image" checked={canvasFormat === 'image'} onChange={() => setCanvasFormat('image')} className="rounded border-white/20 text-violet-500" />
                    <span className="text-white/90">Bild (PNG)</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input type="radio" name="canvasFormat" value="video" checked={canvasFormat === 'video'} onChange={() => setCanvasFormat('video')} className="rounded border-white/20 text-violet-500" />
                    <span className="text-white/90">Video (MP4)</span>
                  </label>
                </div>
              </div>
              <label className="mb-2 block text-sm font-medium text-white/80">Design-Prompt (optional)</label>
              <input
                type="text"
                value={canvasDesignPrompt}
                onChange={(e) => setCanvasDesignPrompt(e.target.value)}
                placeholder="z. B. minimalistisch, Farbverlauf, Stimmung zum Song"
                className="mb-4 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder-white/40 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              />
              <div className="flex gap-3">
                <PrimaryButton onClick={startCanvas} disabled={canvasSubmitting}>
                  {canvasSubmitting ? 'Wird gestartet…' : 'Canvas generieren'}
                </PrimaryButton>
                <SecondaryButton onClick={closeCanvasModal}>Abbrechen</SecondaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  )
}
